import { describeSource, readStampOf, type DataSource } from '@/lib/data'

import { labelOf, sectionOf } from '../nav'

/**
 * Breadcrumb, provenance, title and purpose. Nothing else.
 *
 * # What used to be here, and why it is not (#124)
 *
 * The prototype drew three control clusters on the right and they were
 * transcribed faithfully: a state previewer that forced the screen into
 * `loading` / `empty` / `error` / `denied`, session previewers that painted a
 * standby cell, a degraded provider and a break-glass banner, and a chip per
 * console law the screen answers to.
 *
 * All three are for showing a design to a reviewer. In front of an operator
 * they are hazards rather than clutter: the state previewer renders a refusal
 * that did not happen, and the degraded toggle opened an incident drawer of
 * fabricated incidents naming a payment rail. An operator can act on those.
 *
 * The real states stay — `LoadingView`, `EmptyView`, `ErrorView` and
 * `DeniedView` are what a real read produces. What is gone is the ability to
 * force one from the header.
 *
 * The provenance chip stays, because it is about the data on the screen.
 */

interface Props {
  readonly screenId: string
  /**
   * What the screen calls itself — READ, or the nav label.
   *
   * Never the corpus `title`. See `Console.tsx` where these are derived: a
   * screen that has not read an identity is titled after the place it is, not
   * after a person the design corpus invented.
   */
  readonly title: string
  /** The line under it. Empty renders nothing rather than corpus prose. */
  readonly purpose: string
  /**
   * Where this screen's figures came from. `null` while a live read is still
   * in flight — see `ProvenanceChip`.
   */
  readonly provenance: DataSource | null
}

export function ScreenHeader({ screenId, title, purpose, provenance }: Props) {

  return (
    <div style={{ flex: '0 0 auto', padding: '12px 16px 0', background: 'var(--k-bg)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: '8px',
          fontSize: '10.5px',
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: 'var(--k-mut2)',
        }}
      >
        <span>{sectionOf(screenId)}</span>
        <span style={{ fontFamily: 'var(--k-mono)' }}>/</span>
        <span style={{ color: 'var(--k-mut)' }}>{labelOf(screenId)}</span>
        {provenance !== null && <ProvenanceChip source={provenance} />}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: '10px 16px',
          marginTop: '6px',
          paddingBottom: '8px',
          borderBottom: '2px solid var(--k-rule)',
        }}
      >
        <div style={{ flex: '1 1 340px', minWidth: '290px' }}>
          <h1 style={{ margin: 0, fontSize: '23px', letterSpacing: '-.02em', lineHeight: 1.1 }}>
            {title}
          </h1>
          {purpose !== '' && (
            <div
              style={{
                color: 'var(--k-mut)',
                fontSize: '12px',
                marginTop: '3px',
                maxWidth: '78ch',
                textWrap: 'pretty',
              }}
            >
              {purpose}
            </div>
          )}
        </div>

      </div>
    </div>
  )
}

/**
 * Whether the figures below are real, and if so what answered and when.
 *
 * # Why it is here rather than in the cell bar
 *
 * It used to be a single "Fixtures" marker in the cell bar. The cell bar
 * answers "where am I deployed" — cell, role, environment, open alarms — which
 * is one answer for the whole console and stays true as the operator moves
 * around. Provenance is the opposite kind of fact: it changes with every
 * navigation, because the dashboard reads `/v1/admin` and the other 114 screens
 * are still the design corpus. A per-screen claim in global chrome is how the
 * old marker came to be false in both directions at once.
 *
 * So it sits in the breadcrumb line, next to the section, the screen name and
 * the phase chip — the row that already answers "what am I looking at". It is
 * read in the same left-to-right sweep as the title and before any figure
 * below it, which is the order that matters: a caveat read after the number has
 * already been read is a caveat that did not work.
 *
 * # Why the timestamp is beside the chip and not only in the tooltip
 *
 * Law 12 wants freshness visible on anything the analytics mirror answered for,
 * and a stamp reachable only by hovering is a stamp nobody reads. The chip's
 * `title` carries the full account — store, authority, and which figures were
 * not answered for — because that does not fit on one line; the age does fit,
 * so it is on the line.
 */
function ProvenanceChip({ source }: { source: DataSource }) {
  const { label, tone, title } = describeSource(source)

  return (
    <>
      <span
        data-tone={tone}
        title={title}
        style={{
          marginLeft: '6px',
          padding: '1px 5px',
          fontWeight: 800,
          letterSpacing: '.06em',
          // The store and path inside a live label are identifiers an operator
          // may retype into a query; upper-casing them would corrupt them.
          // Same reason the cell bar renders its clock with the transform off.
          textTransform: 'none',
          maxWidth: '46ch',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          cursor: 'help',
        }}
      >
        {label}
      </span>
      {readStampOf(source) !== null && (
        <span
          style={{
            fontFamily: 'var(--k-mono)',
            letterSpacing: '.02em',
            textTransform: 'none',
            color: 'var(--k-mut2)',
          }}
        >
          read {readStampOf(source)}
        </span>
      )}
    </>
  )
}
